/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener("variant:added", async function (event) {
  var variant = event.detail.variant; // Get the variant that was added
  if (!variant.properties?.RecipeID) return;
  const recipeId = variant.properties.RecipeID.split("-")[1];

  const response = await fetch(`https://silhouette.jtbcustom.com/api/v2/recipes/${recipeId}`);
  const data = await response.json();
  const personalizedVariantId = data.variant_id;
  const thumbnail = `https:${data.thumbnail}`;

  const response2 = await fetch("/cart.js");
  const data2 = await response2.json();

  const personalizedVariant = data2.items.find((item) => item.id == personalizedVariantId);
  let key = personalizedVariant?.key;
  let properties = { ...personalizedVariant?.properties, _thumbnail: thumbnail };

  const response3 = await fetch("/cart/change.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id: key,
      properties: properties,
    }),
  });

  const data3 = await response3.json();

  document.documentElement.dispatchEvent(
    new CustomEvent("cart:refresh", {
      bubbles: true,
      detail: {
        cart: data3,
      },
    })
  );
});

document.addEventListener("cart:updated", async function (event) {
  var cart = event.detail.cart; // Get the updated content of the cart

  // console.log("cart:updated ", cart);

  document.documentElement.dispatchEvent(
    new CustomEvent("cart:refresh", {
      bubbles: true,
      detail: {
        cart: cart,
      },
    })
  );
});

document.addEventListener("variant:changed", function (event) {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });

  let variant = event.detail.variant;
  let personalizeButton = document.querySelector(".personalize-scout-button-with-variant");
  let personalizeButtonVariant = document.querySelector(".personalize-scout-button");
  if (personalizeButton) {
    personalizeButton.setAttribute("href", `/pages/personalizer/${variant.id}`);
    if (!variant.available) {
      personalizeButton.style.pointerEvents = "none";
    } else {
      personalizeButton.style.pointerEvents = "initial";
    }
  }
  if (personalizeButtonVariant) {
    personalizeButtonVariant.setAttribute("href", `/pages/personalizer/${variant.id}`);
    if (!variant.available) {
      personalizeButtonVariant.style.pointerEvents = "none";
    } else {
      personalizeButtonVariant.style.pointerEvents = "initial";
    }
  }
});

document.addEventListener("rebuy.add", async function (event) {
  const rebuyCart = window.Rebuy.Cart;
  const response = await fetch(`https://${window.themeVariables.routes.host}/cart.js`);
  const data = await response.json();

  if (event.detail.widget.id === "104481") {
    let tmpBundleId = `tmp-bundle-id-${event.detail.widget.id}-${+new Date()}`;
    event.detail.product.forEach((prod) => {
      tmpBundleId += `-${prod.id}`;
    });

    console.log(rebuyCart.cart.items);

    rebuyCart.cart.items.forEach(async (item) => {
      const customProperties = { ...item.properties, _tmpBundleId: tmpBundleId };
      item.properties = customProperties;

      await rebuyCart.changeItem({
        id: item.key,
        quantity: 1,
        properties: item.properties,
      });
    });

    setTimeout(() => {
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: rebuyCart.cart,
          },
        })
      );
    }, 1000);
  } else {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          cart: data,
        },
      })
    );
  }

  document.getElementById("mini-cart").open = true;
});

// Listen for the rebuy:cart.change event
document.addEventListener("rebuy:cart.change", () => {
  setTimeout(() => {
    const SmartCart = window.Rebuy?.SmartCart;
    const cartEl = document.querySelector(".rebuy-cart__flyout");
    if (!SmartCart || !cartEl) return;

    // Helpers
    const updateText = (selector, text) => {
      const el = cartEl.querySelector(selector);
      if (el) el.textContent = text;
    };

    const initialPrice = SmartCart.subtotalNumber();
    const discountPrice = SmartCart.getCalculatedCartDiscount();
    const finalPrice = initialPrice - discountPrice;
    const cartTotal = finalPrice / 100;

    // Free shipping / fee logic
    const freeShippingTier = SmartCart.progressBar?.tiers.find(
      (tier) => tier.type === "shipping"
    );

    if (freeShippingTier && cartTotal >= freeShippingTier.minimum) {
      updateText("#smart-cart-shipping-fee", "FREE!");
    } else if (cartTotal < 50) {
      updateText("#smart-cart-shipping-fee", "Calculated At Checkout");
    } else if (cartTotal < 99) {
      updateText("#smart-cart-shipping-fee", "Calculated At Checkout");
    }

    // Price + labels
    updateText("#smart-cart-initial-price", `$${(initialPrice / 100).toFixed(2)}`);
    updateText("#smart-cart-auto-discount-price", `-$${(discountPrice / 100).toFixed(2)}`);
    updateText("#smart-cart-subtotal-price", `$${(finalPrice / 100).toFixed(2)}`);
    updateText("#smart-cart-checkout-btn-text", `$${(finalPrice / 100).toFixed(2)}`);
    updateText(".primary-title", "OTHERS ALSO BOUGHT");
    updateText("#rebuy-cart-title", `Your cart (${window.Rebuy.Cart?.cart?.item_count || 0})`);

    // Delayed DOM tweaks
    setTimeout(() => {
      const fixedBar = cartEl.querySelector(".rebuy-smart-cart-fixed-bar-header");
      if (fixedBar) {
        cartEl.append(fixedBar);
        fixedBar.classList.remove("hidden");
      }

      const emptyCart = cartEl.querySelector(".rebuy-cart__flyout-empty-cart");
      const emptyGrid = cartEl.querySelector(".empty-cart-grid-collection-wrapper");
      if (emptyCart && emptyGrid && !emptyCart.contains(emptyGrid)) {
        emptyCart.appendChild(emptyGrid);
        emptyGrid.classList.remove("hidden");
      }

      // Toggle visibility depending on empty cart
      const toggleEl = (selector, hide) => {
        const el = cartEl.querySelector(selector);
        if (el) el.classList.toggle("hidden", hide);
      };

      if (emptyCart) {
        toggleEl('[data-rebuy-component="cross-sell-widget"]', true);
        toggleEl('[data-rebuy-cart-anchor="footer"]', true);
        toggleEl(".empty-no-hr", true);
      } else {
        toggleEl('[data-rebuy-component="cross-sell-widget"]', false);
        toggleEl('[data-rebuy-cart-anchor="footer"]', false);
        toggleEl(".empty-no-hr", false);
      }
    }, 1000);

    // Replace remove icons
    const removeIcons = cartEl.querySelectorAll(".rebuy-cart__flyout-item-remove");
    removeIcons.forEach((icon) => {
      icon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 17 18" fill="none">
          <path d="M15.7529 3.3648H0.702881M6.17561 7.46941V12.9421M10.2801 7.46941V12.9421M14.3847 3.3648V16.3625C14.3847 16.544 14.3126 16.718 14.1844 16.8463C14.0561 16.9745 13.8821 17.0466 13.7006 17.0466H2.75518C2.57374 17.0466 2.39974 16.9745 2.27145 16.8463C2.14316 16.718 2.07108 16.544 2.07108 16.3625V3.3648M11.6483 3.36484V1.99666C11.6483 1.6338 11.5042 1.28579 11.2476 1.02921C10.991 0.772626 10.643 0.628479 10.2801 0.628479H6.17559C5.81272 0.628479 5.46472 0.772626 5.20814 1.02921C4.95155 1.28579 4.80741 1.6338 4.80741 1.99666V3.36484" 
          stroke="black" stroke-width="0.843137" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
    });
  }, 500);
});

var MY_SELECTOR = "#webWidget";
var observer = new MutationObserver(function (mutations) {
  for (let mutation of mutations) {
    for (let node of mutation.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;

      if (node.matches(MY_SELECTOR)) {
        if (document.querySelector("#mini-cart[open]")) {
          node.style.left = "0";
        }
      }
    }
  }
});

observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
});

document.addEventListener("variant:changed", async function (event) {
  const variant = event.detail.variant;

  document.querySelectorAll(".color-swatch__item").forEach((swatch) => {
    swatch.classList.remove("color-swatch-item-active");
  });

  const seletedPattern = variant.option1.replace(/ /g, "-").toLowerCase();
  const selectedSwatchValue = variant.option1;

  if (selectedSwatchValue) {
    const sameValueSwatches = document.querySelectorAll(
      `product-variants .color-swatch__radio[value='${selectedSwatchValue}']`
    );

    sameValueSwatches.forEach((swatch) => {
      if (swatch.nextElementSibling) {
        swatch.nextElementSibling.classList.add("color-swatch-item-active");
      }
    });
  }

  const collectionUrl = `https://scoutbags.com/collections/${seletedPattern}`;

  const links = document.querySelectorAll(".more-patterns-link");
  if (links.length > 0) {
    links.forEach((link) => {
      link.setAttribute("href", collectionUrl);
    });
  }

  const customPatternName = document.querySelector("#custom-product-detail-pattern");
  if (customPatternName && variant && variant.option1) {
    customPatternName.textContent = variant.option1;
  }

  var pattern = seletedPattern;
  var productID = document.querySelector(".yotpo.yotpo-pictures-widget").getAttribute("data-product-id");
  if(variant.featured_image && variant.featured_image.product_id){
    productID = variant.featured_image.product_id;
  }
  const rebuy_data_source_bundle = document.querySelector(".yotpo.yotpo-pictures-widget").getAttribute("data-rebuy-data-source-bundle-id");
  
  const featuredProductsList = document.querySelector('.product-content__featured-products-list');
  featuredProductsList.innerHTML = '';
  const rebuyPR = await fetch('https://rebuyengine.com/api/v1/custom/id/'+rebuy_data_source_bundle+'?shopify_product_ids='+productID+'&format=pretty&key=a9f335b204f19a4c16d670f9d041011168d2bbaa');
  const rebuyJson = await rebuyPR.json();
  const count = 3;

  console.log('REBUY PR', rebuyPR)
  console.log('REBUY JSON', rebuyJson)
  console.log('REBUY JSON LEN', rebuyJson.data.length)

  
  var product_change = '';
  if(rebuyJson.data.length > 0){
    for (let i = 0; i < rebuyJson.data.length && i < count; i++) {
      let handle = rebuyJson.data[i].handle;
      
      let pattern_rebuy = rebuyJson.data[i].options[0].values[0].replace(/ /g, "-").toLowerCase();
      let newPattern = handle.replace(pattern_rebuy, '');
      let newHandle = newPattern + pattern;
      var prUrl = `https://scoutbags.com/products/${newHandle}?view=product-item-CTS`;
      var response  = await fetch(prUrl);

      console.log('REBUY newHandle', newHandle)
      console.log('REBUY handle', handle)
      console.log('REBUY response', response)
      if(!response.ok){
        prUrl = `https://scoutbags.com/products/${handle}?view=product-item-CTS`;
        response  = await fetch(prUrl);
      }
      let result = await response.text();
      let productItem = new DOMParser().parseFromString(result, 'text/html').querySelector(`product-item`).innerHTML;
      console.log('product item', productItem)
      let productItemContainer = document.createElement('product-item');
      productItemContainer.classList.add('product-item');
      productItemContainer.innerHTML = productItem;
      // productItemContainer.getElementsByClassName("product-item__primary-image")[0].remove();
      // productItemContainer.getElementsByClassName("product-item__secondary-image")[0].classList.add('product-item__primary-image__bundle');
      // productItemContainer.getElementsByClassName("product-item__primary-image__bundle")[0].classList.remove('product-item__secondary-image');
      // productItemContainer.getElementsByClassName("product-item__primary-image__bundle")[0].setAttribute("data-handle", pattern);

      const primaryImage = productItemContainer.getElementsByClassName("product-item__primary-image")[0];
      if (primaryImage) primaryImage.remove();

      const secondaryImage = productItemContainer.getElementsByClassName("product-item__secondary-image")[0];
      if (secondaryImage) {
        secondaryImage.classList.add('product-item__primary-image__bundle');
        secondaryImage.classList.remove('product-item__secondary-image');
        secondaryImage.setAttribute("data-handle", pattern);
      }

      product_change += '<product-item class="product-item">'+productItemContainer.innerHTML+'</product-item>';

    }
    featuredProductsList.innerHTML = product_change;
    const featuredProductsList_ = document.querySelector('.product-content__featured-products');
    featuredProductsList_.style.display = "block"
  }else{
    const featuredProductsList = document.querySelector('.product-content__featured-products');
    featuredProductsList.style.display = "none"
  }
});

document.addEventListener("DOMContentLoaded", (event) => {
  const inputElement = document.querySelector("#product-quantity-input");
  const customQty = document.querySelector("#custom-product-detail-qty");

  if (inputElement && customQty) {
    inputElement.addEventListener("change", function () {
      customQty.textContent = inputElement.value;
    });
  }
});

document.addEventListener("DOMContentLoaded", function() {
  setTimeout(() => {


    const rebuyCartElement = document.querySelector("#rebuy-cart");
    const personalizationWarning = document.querySelector("#personalization-warning");

    if (!rebuyCartElement) {
      console.error("❌ #rebuy-cart not found");
      return;
    }
    if (!personalizationWarning) {
      console.error("❌ #personalization-warning not found");
      return;
    }

    if (rebuyCartElement.innerText.includes("RecipeID")) {
      personalizationWarning.style.display = "block";
      clearInterval(intervalId);
    }

  }, 3000);
});

// Reusable function to attach swatch listeners to product items
function attachSwatchListeners(container) {
  const productItems = container.querySelectorAll("product-item");

  productItems.forEach((productItem) => {
    // Skip if already initialized
    if (productItem.hasAttribute("data-swatch-initialized")) {
      return;
    }
    productItem.setAttribute("data-swatch-initialized", "true");

    const swatches = productItem.querySelectorAll(".block-swatch .color-swatch__item");

    swatches.forEach((swatch) => {
      swatch.addEventListener("click", function(event) {
        // Extract variant ID from href
        const href = swatch.getAttribute("href");
        const variantId = href?.match(/variant=(\d+)/)?.[1];

        if (variantId) {
          // Update the hidden variant ID input in both desktop hover and mobile forms
          const variantIdInputs = productItem.querySelectorAll(".product-item-variant-id");
          variantIdInputs.forEach((input) => {
            input.value = variantId;
          });
        }
      });
    });
  });
}

// Attach swatch listeners on initial page load
document.addEventListener("DOMContentLoaded", function() {
  attachSwatchListeners(document);
});

// Watch for dynamically loaded products (AJAX filtering, pagination, etc.)
document.addEventListener("DOMContentLoaded", function() {
  // Watch for changes in the main collection product list
  const facetMain = document.querySelector("#facet-main");

  if (facetMain) {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // New content has been added, reattach swatch listeners
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              attachSwatchListeners(node);
            }
          });
        }
      });
    });

    // Observe the facet-main container for changes
    observer.observe(facetMain, {
      childList: true,
      subtree: true
    });
  }
});